/* eslint-disable import/no-named-as-default */
"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { AnimatePresence, motion } from "framer-motion";
import { signIn } from "next-auth/react";
import { useTranslations } from "next-intl";
import React, { useState } from "react";
import { useForm } from "react-hook-form";

import AgentAuthForm from "./AgentAuthForm";
import { formFieldVariants } from "./animations";
import { AuthFormValues, authSchema } from "./schemas";
import FormOtpField from "../common/forms/FormOtpField";
import FormPhoneField from "../common/forms/FormPhoneField";
import FormTextField from "../common/forms/FormTextField";
import { DialogTitle } from "../ui/dialog";
import { CheckPhoneNumberResponse } from "@/app/api/auth/check-number/route";
import { AUTH_TYPES } from "@/lib/auth/constants";
import Button from "@/components/ui/buttons/Button";
import { useRouter } from "@/i18n/routing";
import { clientAuthService } from "@/lib/api/auth/auth";
import { cn } from "@/lib/utils";
import { REGISTER_ERRORS } from "@/utils/constants";

interface AuthFormProps {
  favorites: Set<string>;
  closeDialog: () => void;
  clearFavorites: () => void;
  isPage?: boolean;
  /** Where to send the user after an agent login started from the standalone login page. */
  callbackUrl?: string;
}

const AuthForm: React.FC<AuthFormProps> = ({ favorites, closeDialog, clearFavorites, isPage = false, callbackUrl }) => {
  const [authType, setAuthType] = useState<"login" | "register">("login");
  const [isAgentLogin, setIsAgentLogin] = useState(false);
  const [formMessage, setFormMessage] = useState<{ message: string; type: "info" | "error" }>({
    message: "",
    type: "info",
  });
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [otpError, setOtpError] = useState("");

  const [loadingState, setLoadingState] = useState({
    isLoginLoading: false,
    isOtpLoading: false,
  });

  const router = useRouter();
  const t = useTranslations("Login");

  const authForm = useForm<AuthFormValues>({
    resolver: zodResolver(authSchema),
    defaultValues: {
      authType: "login",
      phone: "",
      code: "",
      savedFavoriteVehicles: [],
    },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const handleSendOTP = async (phone: string) => {
    setLoadingState({ ...loadingState, isOtpLoading: true });

    try {
      const checkPhoneNumberResponse = await fetch("/api/auth/check-number", {
        method: "POST",
        body: JSON.stringify({ phone }),
      });

      const phoneNumberCheck = (await checkPhoneNumberResponse.json()) as CheckPhoneNumberResponse;

      if (!phoneNumberCheck.exists) {
        // Phone number does not exist, so we need to ask for the name and email (which will be optional)
        authForm.setValue("authType", "register");
        setAuthType("register");
        setFormMessage({
          message: "Please fill in the rest of your details to continue",
          type: "info",
        });
      }

      const response = await fetch("/api/auth/send-otp", {
        method: "POST",
        //* pass isRegistration to skip the check if the phone number exists (done already in the check-number route)
        body: JSON.stringify({ phone, isRegistration: true }),
      });

      const data = await response.json();

      if (data?.error) {
        setOtpError((_) => data?.details?.message ?? "Error while submitting OTP request");
        setTimeout(() => {
          setOtpError("");
        }, 2000);
        return;
      }

      setIsOtpSent(true);

      if (authForm.getValues("authType") === "login") {
        authForm.reset({
          authType: "login",
          phone: phone,
          code: "",
        });
      } else {
        authForm.reset({
          authType: "register",
          phone: phone,
          code: "",
          name: "",
          ...(data.email ? { email: data.email } : {}),
        });
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
    } finally {
      setLoadingState({ ...loadingState, isOtpLoading: false });
    }
  };

  //* Agents keep working where they were: the dialog just closes and re-renders the current page,
  //* while the standalone login page falls back to the callback url it was opened with.
  const handleAgentAuthenticated = () => {
    if (isPage) {
      router.replace(callbackUrl || "/");
      return;
    }

    closeDialog();
    router.refresh();
  };

  const handleToggleAgentLogin = () => {
    setIsAgentLogin((previous) => !previous);
    setFormMessage({ message: "", type: "info" });
  };

  const onLoginSubmit = async (data: AuthFormValues) => {
    setLoadingState({ ...loadingState, isLoginLoading: true });

    try {
      if (favorites.size > 0) {
        data.savedFavoriteVehicles = Array.from(favorites);
      }

      if (authType === "register" && data.authType === "register") {
        const registerResponse = await clientAuthService.register({
          ...data,
          email: data.email ?? undefined,
        });

        if (registerResponse.error === REGISTER_ERRORS.USER_ALREADY_EXISTS) {
          setFormMessage({
            message: "This email is already in use.",
            type: "error",
          });
          return;
        }
      }

      let response: any;

      response = await signIn(AUTH_TYPES.OTP, {
        ...data,
        redirect: true,
        redirectTo: "/my-profile",
      });

      if (response?.error) {
        console.error("Login error:", response.error);
        throw Error(response.error);
      }

      if (response?.ok) {
        closeDialog();
        clearFavorites();
        router.push("/my-profile");
      }
    } catch (error: any) {
      setFormMessage({
        message: "Unable to verify your phone number.",
        type: "error",
      });
      console.log(error);
    } finally {
      setLoadingState({ ...loadingState, isLoginLoading: false });
    }
  };

  return (
    <>
      {isPage ? (
        <>
          <h1 className="urbanist text-center text-3xl font-semibold">
            <span>{isAgentLogin ? t("agent.title") : t("accessClientPortal")}</span>
          </h1>
          <span className="inter text-center text-primary-foreground">
            {isAgentLogin ? t("agent.subtitle") : t("loginOrRegisterAsNewUser")}
          </span>
        </>
      ) : (
        <div className="text-center">
          <DialogTitle className="urbanist text-center text-3xl font-semibold">
            <span>{isAgentLogin ? t("agent.title") : t("accessClientPortal")}</span>
          </DialogTitle>
          <span className="inter text-primary-foreground">
            {isAgentLogin ? t("agent.subtitle") : t("loginOrRegisterAsNewUser")}
          </span>
        </div>
      )}

      <div className={cn("mx-auto w-full", !isPage ? "md:max-w-[80%]" : "")}>
        <div className="inter flex items-center justify-between gap-3 rounded-2xl bg-secondary-light px-4 py-3">
          <label htmlFor="agent-login-toggle" className="cursor-pointer text-sm font-semibold text-black">
            {t("agent.toggleLabel")}
          </label>
          <button
            id="agent-login-toggle"
            type="button"
            role="switch"
            aria-checked={isAgentLogin}
            aria-label={t("agent.toggleLabel")}
            onClick={handleToggleAgentLogin}
            className={cn(
              "relative h-6 w-11 shrink-0 rounded-full transition-colors duration-300",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
              isAgentLogin ? "bg-primary" : "bg-grey-border",
            )}
          >
            <span
              className={cn(
                "absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-all duration-300",
                isAgentLogin ? "left-[1.375rem]" : "left-0.5",
              )}
            />
          </button>
        </div>
      </div>

      {isAgentLogin ? (
        <AgentAuthForm onAuthenticated={handleAgentAuthenticated} isPage={isPage} />
      ) : (
        <>
          {formMessage.message ? (
            <div className="flex items-center justify-center">
              <span
                className={`inter text-sm ${formMessage.type === "error" ? "text-red-500" : "text-secondary-foreground"}`}
              >
                {formMessage.message}
              </span>
            </div>
          ) : null}

          <form onSubmit={authForm.handleSubmit(onLoginSubmit)} className="space-y-4">
            <div className="flex flex-col">
              <div className={cn("mx-auto w-full space-y-4", !isPage ? "md:max-w-[80%]" : "")}>
                <FormPhoneField
                  control={authForm.control}
                  name="phone"
                  placeholder={t("phonePlaceholder")}
                  className="rtl:placeholder:text-right"
                  disabled={isOtpSent}
                  required
                />

                <AnimatePresence mode="sync">
                  {isOtpSent && authType === "register" && (
                    <motion.div variants={formFieldVariants} initial="initial" animate="animate" exit="exit">
                      <div className="space-y-4">
                        <FormTextField
                          control={authForm.control}
                          name="name"
                          placeholder={t("namePlaceholder")}
                          className="rtl:placeholder:text-right"
                          type="text"
                          required
                        />
                        <FormTextField
                          control={authForm.control}
                          name="email"
                          placeholder={t("emailPlaceholder")}
                          className="rtl:placeholder:text-right"
                          type="email"
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <AnimatePresence mode="sync">
                  {isOtpSent && (
                    <motion.div variants={formFieldVariants} initial="initial" animate="animate" exit="exit">
                      <FormOtpField control={authForm.control} name="code" length={6} label="OTP" required />
                    </motion.div>
                  )}
                </AnimatePresence>

                <Button
                  buttonText={isOtpSent ? t("login") : t("sendOtp")}
                  className="mt-10 w-full rounded-lg bg-primary py-3 text-center text-white transition-all duration-300 hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
                  loadingState={isOtpSent ? loadingState.isLoginLoading : loadingState.isOtpLoading}
                  onClick={
                    isOtpSent ? authForm.handleSubmit(onLoginSubmit) : () => handleSendOTP(authForm.getValues("phone"))
                  }
                />
              </div>
              {otpError ? <span className="inter mt-6 text-center text-primary">{otpError}</span> : null}
            </div>
          </form>
        </>
      )}
    </>
  );
};

export default AuthForm;
